import React from 'react';
import { Book, Code, GitBranch, Zap, Mail } from 'lucide-react';
import './About.css';

const About = () => {
  return (
    <div className="about-container">
      <div className="about-content">
        {/* Hero Section */}
        <section className="hero-section">
          <h1>About RenderRepo</h1>
          <p className="hero-subtitle">
            Understand any GitHub repo in minutes with grounded, cited answers.
            Map architecture faster, see where changes should land, and build
            feature plans tied to files RenderRepo actually analyzed—not generic
            narration.
          </p>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <h2>What RenderRepo Does</h2>
          <p className="about-preface">
            You submit a GitHub repository; we analyze a capped set of files and
            build structure (summaries, graphs, and searchable chunks). Query
            answers and citations use only that analyzed slice—so you onboard and
            navigate with file-level receipts, not open-web guesswork. Feature
            plans draw from the same evidence so steps point at real paths in
            your tree.
          </p>
          <div className="features-grid">
            <div className="feature-card">
              <GitBranch className="feature-icon" />
              <h3>Repository Analysis</h3>
              <p>
                Analyze a bounded subset of repository files with transparent
                coverage and caps on large codebases—so you always know what was
                in scope before you trust an answer or a plan.
              </p>
            </div>
            <div className="feature-card">
              <Code className="feature-icon" />
              <h3>Architecture Overview &amp; Flows</h3>
              <p>
                Surface frameworks, entrypoints, tests, and execution flows from
                your analyzed tree—faster orientation when the repo is new or
                unfamiliar.
              </p>
            </div>
            <div className="feature-card">
              <Zap className="feature-icon" />
              <h3>Repo-aware Assistance</h3>
              <p>
                Ask questions with answers grounded in analyzed code, docs, and
                metadata; optional processors extend retrieval. Notes and feature
                plans stay on the repository so decisions and next steps stay tied
                to evidence you can open in the editor.
              </p>
            </div>
          </div>
        </section>

        {/* How Analysis Works */}
        <section className="how-analysis-works-section">
          <h2>How Analysis Works</h2>
          <ul className="about-bullet-list">
            <li>Large repos are capped: we analyze a subset of eligible files so runs stay predictable and scope stays explicit.</li>
            <li>Coverage is shown on every repo—you see what was analyzed, not an implied whole-tree read.</li>
            <li>Deterministic structure (entrypoints, flows, frameworks) is extracted where supported.</li>
            <li>Unsupported languages may have lower deterministic coverage; summaries still run where possible.</li>
            <li>Optional processors are add-ons you can enable for extra analysis (see Processor Types below).</li>
          </ul>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section">
          <h2>How to Use RenderRepo</h2>
          <div className="steps-container">
            <div className="step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Connect Your Repository</h3>
                <p>Log in, open the Repository Analyzer, and paste a public GitHub repository URL or owner/repo.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Configure Analysis</h3>
                <p>Optionally select additional processors, preview the cost (no charge until you confirm), then start processing when you are ready.</p>
              </div>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Review Results</h3>
                <p>
                  When processing completes, use System Overview, File Explorer,
                  Query Repository, Notes, and Feature Plans to orient quickly,
                  inspect source with summaries, ask file-grounded questions, and
                  capture plans you can trace back to analyzed paths.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Started Section */}
        <section className="getting-started-section">
          <h2>Getting Started</h2>
          <div className="getting-started-content">
            <div className="instruction-block">
              <h3>For New Users</h3>
              <ol>
                <li>Create an account or log in with your existing credentials</li>
                <li>Review your available credits in the dashboard and purchase more if needed</li>
                <li>Navigate to the Repository Analyzer from the main navigation</li>
                <li>Start with a small or medium repository (for example a typical service, library, or CLI), not a very large monorepo</li>
                <li>Paste a public GitHub URL or owner/repo (for example facebook/react)</li>
                <li>Preview the cost, select any optional processors, and confirm to start</li>
                <li>Monitor streaming progress and open the analyzed repository when processing is complete</li>
              </ol>
            </div>
            
            <div className="instruction-block">
              <h3>Understanding Your Results</h3>
              <ul>
                <li><strong>System Overview:</strong> Read frameworks, entrypoints, tests, and execution flows from your analyzed repository—faster mental model before you pick where to edit.</li>
                <li><strong>File Explorer:</strong> Browse structure with file summaries and raw source together so you confirm details before you rely on them in a review or refactor.</li>
                <li><strong>Query Repository:</strong> Ask how the system works; answers cite analyzed files (and line ranges when available) so you verify before you refactor or ship.</li>
                <li><strong>Notes &amp; Feature Plans:</strong> Capture notes and implementation-oriented plans on the repository so next steps stay anchored to the files you indexed.</li>
              </ul>
            </div>

            <div className="instruction-block">
              <h3>Processor Types</h3>
              <ul>
                <li><strong>Optional processors:</strong> Enable additional analysis processors configured for your account or workspace.</li>
                <li><strong>File-level processing:</strong> Apply processors to individual files during analysis when file scope is selected.</li>
                <li><strong>Repository-level processing:</strong> Run processors on the complete repository graph and summaries after file analysis finishes.</li>
                <li><strong>Extensible workflows:</strong> Use processor configuration to extend what RenderRepo extracts from your code without changing your application.</li>
              </ul>
            </div>
          </div>
        </section>

        {/* How Credits Work */}
        <section className="how-credits-work-section">
          <h2>How Credits Work</h2>
          <ul className="about-bullet-list">
            <li><strong>Repository analysis:</strong> You need enough credits to start. Credits are consumed as processing runs (based on actual usage), not as one lump charge the moment you confirm. The preview shows what to expect beforehand, and we verify your balance before work begins.</li>
            <li><strong>Feature plans (Feature Builder):</strong> Credits are deducted only after a plan is successfully generated and validated. Failed or invalid plans do not charge credits.</li>
            <li><strong>Querying:</strong> Querying does not use credits.</li>
            <li>Credits are purchased in packages. Monitor your balance and usage in the dashboard.</li>
          </ul>
        </section>

        {/* Credits & Usage Section — packages and dashboard; see How Credits Work for when deduction happens */}
        <section className="credits-section">
          <h2>Credits &amp; Usage</h2>
          <div className="credits-content">
            <div className="credit-info">
              <Book className="credit-icon" />
              <div>
                <h3>Packages and dashboard</h3>
                <p>Credits are purchased in fixed packages. Monitor your balance and usage in the dashboard and top up as your team analyzes more repositories. Analyzing a repository and building feature plans use credits; querying an analyzed repo does not—see How Credits Work above for timing.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Guarantees */}
        <section className="trust-guarantees-section">
          <h2>Trust Guarantees</h2>
          <ul className="about-bullet-list">
            <li>Answers and citations are grounded only in analyzed files for that repository—no mixing in unrelated sources as repo truth.</li>
            <li>We do not cite file paths outside the analyzed set.</li>
            <li>Coverage is shown on every repo so limits are explicit.</li>
            <li>Citations include file paths and line ranges from analyzed content when available.</li>
            <li>If we don&apos;t have relevant analyzed context, we say so instead of guessing.</li>
          </ul>
        </section>

        {/* Contact */}
        <section className="contact-section">
          <h2>Contact</h2>
          <p>For questions or issues, contact the founder:</p>
          <p className="contact-email">
            <Mail className="contact-email-icon" aria-hidden />
            <a href="mailto:renderrepo@gmail.com">renderrepo@gmail.com</a>
          </p>
        </section>

        {/* Support Section */}
        <section className="support-section">
          <h2>Need Help?</h2>
          <p>If you have questions or need assistance:</p>
          <ul>
            <li>Check the dashboard for repository processing status, progress, and usage details</li>
            <li>Review your credit balance and purchase additional credits from the dashboard if needed</li>
            <li>For technical issues or questions about results, use the contact email above</li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default About;